import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { Building2, CheckCircle2, Target, TrendingUp, Clock } from 'lucide-react';
import { constructionServiceSchema, breadcrumbSchema } from '../utils/structuredData';

const ConstructionPage = () => {
  const features = [
    'Pre-works condition surveys',
    'Progress capture during construction',
    'Post-completion and handover documentation',
    'Point cloud surveys for accurate verification',
    'Gridlines & datums for setting-out',
    'Revit & BIM modelling from scan data'
  ];

  const benefits = [
    {
      icon: Target,
      title: 'Fewer Site Revisits',
      description: 'Remote review and verification capabilities'
    },
    {
      icon: TrendingUp,
      title: 'Better Coordination',
      description: 'Single source of truth across all trades'
    },
    {
      icon: Clock,
      title: 'Reduced Risk',
      description: 'Stronger protection against disputes and claims'
    }
  ];

  const caseStudies = [
    {
      title: 'Commercial Office Building – London',
      subtitle: 'Digital Progress Capture & As-Built Coordination',
      challenge: 'A large commercial office building underwent phased refurbishment while partially operational. Multiple contractors, tight access constraints, and need for clear progress reporting without increasing disruption.',
      approach: 'Structured programme of 3D reality capture aligned with construction milestones, creating time-stamped digital records at each stage.',
      deliverables: 'Measurable point cloud datasets, Revit models, updated gridlines and datums, 3D virtual tours for remote review',
      outcomes: [
        'Reduced site visits for consultants and stakeholders',
        'Improved coordination across multiple contractors',
        'Faster resolution of design queries',
        'Clear, defensible progress records',
        'Permanent digital asset for facilities management'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Construction 3D Scanning Services | BIM & Point Cloud Surveys London"
        description="3D laser scanning for London construction projects. Point cloud surveys, BIM modelling and as-built documentation. Get a free quote today."
        keywords="3D scanning construction, laser scanning London, point cloud surveys, BIM modelling, construction technology, as-built documentation, construction progress monitoring, 3D reality capture"
        canonicalUrl={typeof window !== 'undefined' ? `${window.location.origin}/construction` : ''}
        structuredData={[
          constructionServiceSchema,
          breadcrumbSchema([
            { name: 'Home', url: typeof window !== 'undefined' ? window.location.origin : '' },
            { name: 'Construction', url: typeof window !== 'undefined' ? `${window.location.origin}/construction` : '' }
          ])
        ]}
      />
      <Navbar scrolled={true} />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-blue-50 rounded-full mb-6">
              <Building2 className="h-6 w-6 text-blue-600" />
              <span className="text-blue-600 font-semibold">CONSTRUCTION SERVICES</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Construction Projects Powered by 3D Technology
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Modern construction projects demand more than drawings and assumptions. When programmes are tight and sites are complex, accurate digital information becomes critical.
            </p>
          </div>

          {/* Hero Image */}
          <div className="relative rounded-2xl overflow-hidden shadow-2xl mb-16">
            <img
              src="https://images.unsplash.com/photo-1599995903128-531fc7fb694b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwxfHxjb25zdHJ1Y3Rpb24lMjBzaXRlfGVufDB8fHx8MTc3MjY5NDkyNnww&ixlib=rb-4.1.0&q=85&w=1400"
              alt="3D laser scanning on an active construction site in London"
              className="w-full h-[500px] object-cover"
            />
          </div>

          {/* Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start mb-20">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Capturing Construction at Every Stage
              </h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  We use advanced 3D reality capture technologies to record construction projects at every stage — creating reliable, measurable digital records that support design coordination, site management and long-term asset value.
                </p>
                <p>
                  From pre-construction through to handover, we digitally capture sites to reflect their true, as-built condition at a specific moment in time. Each capture creates a permanent, time-stamped record that can be revisited, measured and shared without returning to site.
                </p>
                <p className="font-semibold text-gray-900 mt-6">
                  "3D progress reports are essential for us now. Digital Scanning Group provided accurate and cost-effective drawings, turned things around fast, and were always at the end of the phone when we needed them."
                </p>
                <p className="text-sm text-blue-600">— Project Manager, London</p>
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Our Construction Services</h3>
              <div className="space-y-4">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg">
                    <CheckCircle2 className="h-6 w-6 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 font-medium">{feature}</span>
                  </div>
                ))}
              </div>
              <button className="mt-8 w-full px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-semibold rounded-xl hover:shadow-xl hover:shadow-blue-500/50 transition-all duration-300">
                Request a Quote
              </button>
            </div>
          </div>

          {/* Benefits Section */}
          <div className="mb-20">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">Why Construction Teams Choose Us</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => {
                const Icon = benefit.icon;
                return (
                  <div key={index} className="p-8 bg-blue-50 rounded-2xl text-center">
                    <div className="inline-flex p-4 bg-blue-100 rounded-xl mb-4">
                      <Icon className="h-8 w-8 text-blue-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                    <p className="text-gray-600">{benefit.description}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Case Studies Section */}
          <div className="mb-20">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">Case Study</h2>
            <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
              See how we helped deliver a major London commercial project with 3D technology
            </p>
            {caseStudies.map((study, index) => (
              <div key={index} className="bg-white border-2 border-gray-100 rounded-2xl p-8 mb-8">
                <h3 className="text-3xl font-bold text-gray-900 mb-2">{study.title}</h3>
                <p className="text-blue-600 font-semibold text-lg mb-6">{study.subtitle}</p>
                
                <div className="space-y-6">
                  <div>
                    <h4 className="font-bold text-gray-900 text-lg mb-2">The Challenge</h4>
                    <p className="text-gray-600">{study.challenge}</p>
                  </div>
                  
                  <div>
                    <h4 className="font-bold text-gray-900 text-lg mb-2">Our Approach</h4>
                    <p className="text-gray-600">{study.approach}</p>
                  </div>
                  
                  <div>
                    <h4 className="font-bold text-gray-900 text-lg mb-2">What We Delivered</h4>
                    <p className="text-gray-600">{study.deliverables}</p>
                  </div>
                  
                  <div className="bg-blue-50 rounded-xl p-6">
                    <h4 className="font-bold text-blue-900 text-lg mb-3">Outcomes & Benefits</h4>
                    <ul className="space-y-2">
                      {study.outcomes.map((outcome, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-gray-700">
                          <span className="text-blue-600 mt-1">✓</span>
                          {outcome}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Interactive 3D Model Example */}
          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-12">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">Interactive 3D Model Example</h2>
            <p className="text-gray-600 text-center mb-8 max-w-2xl mx-auto">
              Explore our 3D scan of the Oxford Science Building Project directly below
            </p>
            <div className="bg-white rounded-2xl p-4 shadow-2xl">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Oxford Science Building Project
              </h3>
              <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  src="https://digitalscanninggroup.com/3d-model/oxford-science-building-project/skinned/"
                  className="absolute top-0 left-0 w-full h-full rounded-xl border-2 border-gray-200"
                  title="Oxford Science Building Project - 3D Model"
                  allowFullScreen
                  loading="lazy"
                />
              </div>
              <p className="text-gray-600 mt-4 text-center">
                Use your mouse to navigate • Scroll to zoom • Click and drag to rotate
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ConstructionPage;
